/* ---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { __read, __values } from "tslib";
import { markTracked, trackDisposable, } from '@tencent/dispose-tree';
import { LinkedList } from '../data-structure/linked-list';
var Emitter = /** @class */ (function () {
    function Emitter(options) {
        this.disposed = false;
        this.options = options;
    }
    Object.defineProperty(Emitter.prototype, "event", {
        /**
           * 可用于订阅该 Emitter 的事件
           */
        get: function () {
            var _this = this;
            if (!this.mEvent) {
                this.mEvent = function (listener, thisArgs, disposables) {
                    var _a, _b, _c;
                    if (!_this.listeners) {
                        _this.listeners = new LinkedList();
                    }
                    var firstListener = _this.listeners.isEmpty();
                    if (firstListener && ((_a = _this.options) === null || _a === void 0 ? void 0 : _a.onFirstListenerAdd)) {
                        _this.options.onFirstListenerAdd(_this);
                    }
                    var remove = _this.listeners.push(!thisArgs ? listener : [listener, thisArgs]);
                    if (firstListener && ((_b = _this.options) === null || _b === void 0 ? void 0 : _b.onFirstListenerDidAdd)) {
                        _this.options.onFirstListenerDidAdd(_this);
                    }
                    if ((_c = _this.options) === null || _c === void 0 ? void 0 : _c.onListenerDidAdd) {
                        _this.options.onListenerDidAdd(_this, listener, thisArgs);
                    }
                    var result = {
                        dispose: function () {
                            var _a;
                            if (!_this.disposed) {
                                remove();
                                if ((_a = _this.options) === null || _a === void 0 ? void 0 : _a.onLastListenerRemove) {
                                    var hasListeners = (_this.listeners && !_this.listeners.isEmpty());
                                    if (!hasListeners) {
                                        _this.options.onLastListenerRemove(_this);
                                    }
                                }
                            }
                            markTracked(result);
                        },
                        disposableId: 'event',
                    };
                    if (Array.isArray(disposables)) {
                        disposables.push(result);
                    }
                    trackDisposable(result);
                    return result;
                };
            }
            return this.mEvent;
        },
        enumerable: false,
        configurable: true
    });
    /**
       * To be kept private to fire an event to
       * subscribers
       */
    Emitter.prototype.fire = function (event) {
        var e_1, _a;
        if (this.listeners) {
            // put all [listener,event]-pairs into delivery queue
            // then emit all event. an inner/nested event might be
            // the driver of this
            if (!this.deliveryQueue) {
                this.deliveryQueue = new LinkedList();
            }
            try {
                for (var _b = __values(this.listeners), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var listener = _c.value;
                    this.deliveryQueue.push([listener, event]);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
            while (this.deliveryQueue.size > 0) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                var _d = __read(this.deliveryQueue.shift(), 2), listener = _d[0], event_1 = _d[1];
                try {
                    if (typeof listener === 'function') {
                        listener.call(undefined, event_1);
                    }
                    else {
                        listener[0].call(listener[1], event_1);
                    }
                }
                catch (e) {
                    console.error(e);
                }
            }
        }
    };
    Emitter.prototype.dispose = function () {
        if (this.listeners) {
            this.listeners.clear();
        }
        if (this.deliveryQueue) {
            this.deliveryQueue.clear();
        }
        this.disposed = true;
    };
    return Emitter;
}());
export { Emitter };
